// Two kinds of running week:
//  - RACE: a distance build-up to a 10K, anchored BACKWARD from race week so it always
//    fits the race date. Tables are indexed by weeksUntilRace (0 = race week).
//  - STEADY: no race ahead (none set, or it already happened). A repeating 4-week week
//    shape that follows Bigger Leaner Stronger's cardio rules: 1-3 h a week, mostly easy,
//    at most one hard session, running no more than half (p.333-336); cardio on upper-body
//    days or off days, never leg days, lift first (p.358).
// Kenny's runs sit on his Upper Body A (Wed) and Upper Body B (Fri) days and Saturday.
// Cassidy is distance only.

export const TENK_MILES = 6.21371;
export const DEFAULT_EASY_MPH = 5.5;
export const PLAN_WEEKS = 6;

const LONG_MI    = [6.2, 4.0, 5.5, 5.0, 4.25, 3.5];      // idx = weeksUntilRace
const LONG_PHASE = ['Race', 'Taper', 'Peak', 'Build', 'Build', 'Base'];
const EASY_MI    = [2.0, 2.5, 3.5, 3.0, 3.0, 2.5];
const SPEED = [
  { title: 'Shakeout', kind: 'easy',  detail: 'Easy 15-min shakeout, stay loose' },                     // 0 race week
  { title: 'Sharpener', kind: 'speed', detail: '3 × 400m fast / 90s walk' },                            // 1
  { title: 'Mile test', kind: 'test',  detail: 'Warm up, then 1 mile hard for time + 3 × 200m strides' }, // 2 re-test
  { title: 'Intervals', kind: 'speed', detail: '4 × 600m / 2 min jog' },                                // 3
  { title: 'Intervals', kind: 'speed', detail: '5 × 400m fast / 90s walk' },                            // 4
  { title: 'Mile test', kind: 'test',  detail: 'Warm up, then 1 mile hard for time (baseline)' },       // 5+ baseline
];

const round1 = (n) => Math.round(n * 10) / 10;
const pick = (arr, i) => arr[Math.min(Math.max(0, i), arr.length - 1)];

const dayOnly = (t) => new Date(t.getFullYear(), t.getMonth(), t.getDate());

// True when a race date is set and still ahead (race day itself counts).
export function hasUpcomingRace(raceDate, today = new Date()) {
  if (!raceDate) return false;
  const [y, m, d] = raceDate.split('-').map(Number);
  return new Date(y, m - 1, d) >= dayOnly(today);
}

export function weeksUntilRace(raceDate, today = new Date()) {
  if (!raceDate) return PLAN_WEEKS;
  const [y, m, d] = raceDate.split('-').map(Number);
  const race = new Date(y, m - 1, d);
  const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.max(0, Math.ceil((race - t) / (7 * 86400000)));
}

export function currentPlanWeek(raceDate, today = new Date()) {
  const wutr = weeksUntilRace(raceDate, today);
  return Math.min(PLAN_WEEKS, Math.max(1, PLAN_WEEKS - wutr + 1));
}

// The week to show right now: the race build-up when a race is ahead, else steady.
export function buildCurrentWeek(user, raceDate, today = new Date()) {
  return hasUpcomingRace(raceDate, today)
    ? buildRunWeek(user, weeksUntilRace(raceDate, today))
    : buildSteadyWeek(user, steadyCycleWeek(today));
}

// "Week 3 of 6" during a build-up; "Week 2 of 4" in the steady cycle.
export function weekLabel(raceDate, today = new Date()) {
  return hasUpcomingRace(raceDate, today)
    ? `Week ${currentPlanWeek(raceDate, today)} of ${PLAN_WEEKS}`
    : `Week ${steadyCycleWeek(today)} of ${STEADY_WEEKS}`;
}

// The plan-week number saved on a logged run (plan context).
export function planWeekNumber(raceDate, today = new Date()) {
  return hasUpcomingRace(raceDate, today) ? currentPlanWeek(raceDate, today) : steadyCycleWeek(today);
}

// ── Steady (no race ahead) ───────────────────────────────────────────────────
export const STEADY_WEEKS = 4;
const STEADY_ANCHOR = new Date(2026, 9, 12); // Mon Oct 12 2026 = week 1 of the cycle
const STEADY_SPEED = [
  { title: 'Mile test', kind: 'test', detail: 'Warm up, then 1 mile hard for time + 3 × 200m strides' },
  { title: 'Intervals', kind: 'speed', detail: '5 × 400m fast / 90s walk' },
  { title: 'Intervals', kind: 'speed', detail: '4 × 600m / 2 min jog' },
  { title: 'Intervals', kind: 'speed', detail: '6 × 400m fast / 90s walk' },
];

export function steadyCycleWeek(today = new Date()) {
  const weeks = Math.floor((dayOnly(today) - STEADY_ANCHOR) / (7 * 86400000));
  return (((weeks % STEADY_WEEKS) + STEADY_WEEKS) % STEADY_WEEKS) + 1;
}

export function buildSteadyWeek(user, cycleWeek = 1) {
  const i = Math.min(Math.max(1, cycleWeek), STEADY_WEEKS) - 1;
  if (user === 'kenny') {
    const sp = STEADY_SPEED[i];
    return { steady: true, cycleWeek: i + 1, phase: 'Between races', runs: [
      { slot: 'easy1', weekday: 3, kind: 'easy', title: 'Easy run', distanceMi: 2.5, minutes: 25,
        detail: 'Easy · 2.5 mi conversational. After your lift, 3+ hours later if you can' },
      { slot: 'speed', weekday: 5, kind: sp.kind, title: sp.title, distanceMi: null, minutes: 25,
        detail: `${sp.detail}. The week's one hard session`, isTest: sp.kind === 'test' },
      { slot: 'cardio', weekday: 6, kind: 'lowimpact', title: 'Easy cardio', distanceMi: null, minutes: 50,
        detail: 'Bike, elliptical, rower or incline walk · 50 min easy' },
    ] };
  }
  return { steady: true, cycleWeek: i + 1, phase: 'Steady miles', runs: [
    { slot: 'easy1', weekday: 2, kind: 'easy', title: 'Easy distance', distanceMi: 2.5, detail: 'Easy · 2.5 mi' },
    { slot: 'easy2', weekday: 4, kind: 'easy', title: 'Easy distance', distanceMi: 2.5, detail: 'Easy · 2.5 mi' },
    { slot: 'long', weekday: 6, kind: 'long', title: 'Longer run', distanceMi: 4, detail: 'Long easy · 4 mi' },
  ] };
}

// All four steady weeks, numbered (for "See the plan").
export function buildSteadyPlan(user, today = new Date()) {
  const cur = steadyCycleWeek(today);
  return Array.from({ length: STEADY_WEEKS }, (_, k) => ({ ...buildSteadyWeek(user, k + 1), wk: k + 1, isCurrent: k + 1 === cur }));
}

// One week's runs for a user, given how many weeks out from the race we are.
export function buildRunWeek(user, weeksOut) {
  const i = Math.max(0, weeksOut);
  const long = pick(LONG_MI, i), easy = pick(EASY_MI, i), phase = pick(LONG_PHASE, i);
  const race = i === 0;
  const longRun = {
    slot: 'long', weekday: 6, kind: 'long', title: race ? 'Race day' : 'Long build',
    distanceMi: race ? round1(TENK_MILES) : long,
    detail: race ? '10K race · 6.2 mi' : `Long easy · ${long} mi`, isRace: race,
  };
  if (user === 'kenny') {
    const sp = pick(SPEED, i);
    return { weeksOut: i, phase, runs: [
      { slot: 'easy1', weekday: 3, kind: 'easy', title: 'Easy distance', distanceMi: easy, detail: `Easy · ${easy} mi conversational` },
      { slot: 'speed', weekday: 5, kind: sp.kind, title: sp.title, distanceMi: null, detail: sp.detail, isTest: sp.kind === 'test' },
      longRun,
    ] };
  }
  return { weeksOut: i, phase, runs: [
    { slot: 'easy1', weekday: 2, kind: 'easy', title: 'Easy distance', distanceMi: easy, detail: `Easy · ${easy} mi` },
    { slot: 'easy2', weekday: 4, kind: 'easy', title: 'Easy distance', distanceMi: easy, detail: `Easy · ${easy} mi` },
    longRun,
  ] };
}

// All weeks from the current start down to race week, numbered 1..N (for "See the plan").
export function buildRunPlan(user, raceDate, today = new Date()) {
  const start = Math.min(weeksUntilRace(raceDate, today), PLAN_WEEKS - 1);
  const weeks = [];
  for (let w = start; w >= 0; w--) weeks.push({ ...buildRunWeek(user, w), wk: start - w + 1 });
  return weeks;
}

// ── Pace helpers (kept) ──────────────────────────────────────────────────────
// Treadmill mph that runs a 10K in goalMin minutes (sub-60 -> ~6.2 mph).
export function raceMphFromGoal(goalMin) {
  const g = Number(goalMin);
  if (!g || g <= 0) return 6.2;
  return round1((TENK_MILES * 60) / g);
}

// Adapt the easy anchor from a logged run. Learns only from easy/long runs
// (never intervals/tempo): feels easy (RPE <= 4) -> +0.1; hard (RPE >= 8) -> -0.1.
const ADAPT = { step: 0.1, easyRpe: 4, hardRpe: 8, min: 4.5, max: 8.0, types: ['easy', 'long'] };
export function adaptEasyMph(easyMph, run) {
  const e = Number(easyMph) || DEFAULT_EASY_MPH;
  if (!run || !ADAPT.types.includes(run.type) || run.rpe == null) return e;
  let next = e;
  if (run.rpe <= ADAPT.easyRpe) next = e + ADAPT.step;
  else if (run.rpe >= ADAPT.hardRpe) next = e - ADAPT.step;
  return round1(Math.min(ADAPT.max, Math.max(ADAPT.min, next)));
}
